import { Card, Divider, Grid, Switch } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import FormField from "layouts/pages/account/components/FormField";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import reportsAPI from "api/reports";
import bksAPI from "api/bks";
import fileDownload from "js-file-download";
import { useForm } from "react-hook-form";
import { useRef, useState } from "react";
import Swal from "sweetalert2";

function breakdownValues(input) {
    // console.log(input)
    const start_time = moment(input.start_time);
    const end_time = moment(input.end_time);

    return {
        start_time: start_time.format("HH:mm"),
        start_date: start_time.format("YYYY-MM-DD"),
        end_time: end_time.format("HH:mm"),
        end_date: end_time.format("YYYY-MM-DD"),
    }
}

function combineValues(input) {

    var ret = {
        start_time: moment(input.start_date + ' ' + input.start_time),
        end_time: moment(input.end_date + ' ' + input.end_time)
    };

    return {
        start_time: ret.start_time.toDate(),
        end_time: ret.end_time.toDate(),
    };
}

function DeviceReportingForm({ uuid, reportInputs, setReportInputs }) {

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({ defaultValues: reportInputs });

    bksAPI.loadToken(useAuth0());
    reportsAPI.loadToken(useAuth0());

    const formRef = useRef(null);

    const [timingSeparated, setTimingSeparated] = useState(breakdownValues(reportInputs));

    const triggerFormSubmit = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    }

    const submitForm = (formData) => {
        formData.start_time = moment(reportInputs.start_time).unix();
        formData.end_time = moment(reportInputs.end_time).unix();

        downloadPDF(uuid, formData.start_time, formData.end_time);
    }

    async function downloadPDF(uuid, start_time, end_time) {
        var device = await bksAPI.getDevice(uuid);
        Swal.showLoading();

        const options = {
            responseType: 'blob',
            onDownloadProgress: function (progressEvent) {
                const percentComplete = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                if (percentComplete == 100){
                    Swal.close();
                }
            }
        }

        reportsAPI.getLiveviewHistoryReport(uuid, start_time, end_time, options).then(function (data) {
            var formatted_start_time = moment(start_time * 1000).tz('Australia/Sydney').format('DDMM-HHmm');
            var formatted_end_time = moment(end_time * 1000).tz('Australia/Sydney').format('DDMM-HHmm');

            fileDownload(data, `eMinor${device.name}_${formatted_start_time}_${formatted_end_time}.pdf`);
        });
    };

    function updateValues(field, new_value) {
        // console.log(field, new_value)
        var new_timing = timingSeparated;
        new_timing[field] = new_value;
        setReportInputs(combineValues(new_timing))
    }

    function applyValues(field) {
        return {
            "value": timingSeparated[field],
            "onChange": (new_value) => {
                updateValues(field, new_value.target.value);
            }
        }
    }

    return (
        <Card sx={{ overflow: "visible", height: '100%' }}>
            <SoftBox p={3}>
                <SoftBox>
                    <SoftTypography variant="h5">Device-Based Event Reporting</SoftTypography>
                </SoftBox>
                <SoftBox pt={1}>
                    <SoftTypography variant="body3">This report generator creates a PDF documenting what the device has displayed over a given timeframe. To generate a report, input a valid start date and time, and end date and time.</SoftTypography>
                </SoftBox>
            </SoftBox>
            <SoftBox pb={3} px={3}>
                <form ref={formRef} onSubmit={handleSubmit((data) => { submitForm(data) })}>
                    <Grid container spacing={3}>
                        <Grid item xxs={6}>
                            <SoftBox mb={2}>
                                <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography component="label" variant="button" fontWeight="bold">
                                        Report Start Date
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput type="date" input={{ placeholder: "Select a date" }} {...applyValues('start_date')} />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={6}>
                            <SoftBox mb={2}>
                                <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography component="label" variant="button" fontWeight="bold">
                                        Report Start Time
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} {...applyValues('start_time')} />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={6}>
                            <SoftBox mb={2}>
                                <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography component="label" variant="button" fontWeight="bold">
                                        Report End Date
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput type="date" input={{ placeholder: "Select a date" }} {...applyValues('end_date')} />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={6}>
                            <SoftBox mb={2}>
                                <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography component="label" variant="button" fontWeight="bold">
                                        Report End Time
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} {...applyValues('end_time')} />
                            </SoftBox>
                        </Grid>
                    </Grid>
                    <SoftBox py={3}>
                        <Divider />
                    </SoftBox>
                    <SoftBox display='flex' flexDirection='row' justifyContent='center' alignItems='center'>
                        <SoftButton variant='gradient' color='dark' sx={{ width: '25%' }} onClick={() => { triggerFormSubmit(); }}>
                            Generate Report
                        </SoftButton>
                    </SoftBox>
                </form>
            </SoftBox>
        </Card>
    );
}

DeviceReportingForm.propTypes = {
    uuid: PropTypes.string,
    reportInputs: PropTypes.object,
    setReportInputs: PropTypes.func,
};

export default DeviceReportingForm;